import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Meteor } from 'meteor/meteor';
import i18n from '../../../../i18n';
import {
  connectProperty,
  fetchConnectTarget,
  customerLabel,
  joinNames,
  restaurantLabel,
  undoConnect,
  type ConnectRequest,
  type ConnectResult,
  type ConnectTarget,
  type ConnectUndo,
  type RestaurantOption,
} from '../../data/googleAccounts';
import { useDialogFocus } from '../../lib/useDialogFocus';
import { Icon } from '../shared/Icon';
import { RestaurantCombobox } from './RestaurantCombobox';
import '../../styles/account-connect.css';

const PRODUCT_KEY: Record<string, string> = {
  ga4: 'growth:setup.analytics',
  ads: 'growth:setup.ads',
  gsc: 'growth:setup.gsc',
  gbp: 'growth:setup.gbp',
};

export function productLabel(kind: string): string {
  return i18n.t(PRODUCT_KEY[kind] || kind);
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** What a connect leaves for the toast: its words and how to undo it. */
export type ConnectedToast = {
  restaurant: string;
  property: string;
  /** Several connects at once read as a count. */
  count?: number;
  undo: ConnectUndo[];
};

/**
 * The confirmation every connect goes through (docs/19 section 5): a dialog
 * on desktop, a sheet on a phone. The person picks the first restaurant
 * from the ones they can open; after that only the same customer's
 * restaurants can be added, with the reason. The server says whether the
 * property already reaches a restaurant (skipped) or feeds another customer
 * (refused), and whether Google granted its product; it checks every rule
 * again when connecting.
 */
export function ConnectPropertySheet({
  restaurantId,
  request,
  restaurants,
  onClose,
  onConnected,
}: {
  /** The context's restaurant, which only anchors the calls. */
  restaurantId: string;
  request: ConnectRequest;
  /** Every restaurant the person can open. */
  restaurants: RestaurantOption[];
  onClose: () => void;
  onConnected: (toast: ConnectedToast, result: ConnectResult) => void;
}) {
  const { t } = useTranslation('growth');
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const pickId = useId();
  const nameId = useId();
  useDialogFocus(ref, onClose);
  const [picked, setPicked] = useState<string>(request.initialRestaurantId || '');
  const [target, setTarget] = useState<ConnectTarget | null>(null);
  const [checking, setChecking] = useState(false);
  const [extra, setExtra] = useState<Set<string>>(new Set());
  const [nameDraft, setNameDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const product = productLabel(request.property.kind);
  const byId = useMemo(() => new Map(restaurants.map(r => [r.id, r])), [restaurants]);

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    setError(null);
    setExtra(new Set());
    fetchConnectTarget(restaurantId, request, picked || null)
      .then(next => {
        if (cancelled) return;
        setTarget(next);
        const fallback = next.connectedByName
          ? t('accountConnect.defaultName', { name: next.connectedByName })
          : t('accountConnect.defaultNameAnon');
        setNameDraft(next.target?.connectionName || next.accountName || fallback);
      })
      .catch(e => {
        if (!cancelled) setError(errorText(e));
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId, request.credentialId, request.property.externalId, picked]);

  const info = target?.target ?? null;
  const already = new Set(info?.alreadyRestaurantIds || []);
  // Once other venues are ticked, the first pick may only move within the
  // same customer (rule 3); untick them to choose another customer.
  const sameCustomerOnly = extra.size > 0 && Boolean(info?.venues.length);
  const pickable = useMemo(
    () => (sameCustomerOnly ? (info?.venues || []).filter(v => byId.has(v.id)) : restaurants),
    [sameCustomerOnly, info, byId, restaurants],
  );
  const reasonId = `${pickId}-reason`;
  const siblings = (info?.venues || []).filter(v => v.id !== picked);
  const chosenIds = [picked, ...[...extra]].filter(Boolean);
  const toConnect = chosenIds.filter(id => !already.has(id));
  const names = toConnect.map(id => byId.get(id)?.name || info?.venues.find(v => v.id === id)?.name || id);
  const restaurant = names.length ? joinNames(names, t('accountConnect.and')) : '';
  const pickedOption = picked ? byId.get(picked) || info?.venues.find(v => v.id === picked) : null;
  const refused = Boolean(info?.feedsOtherCustomer);
  const notGranted = target ? !target.productGranted : false;
  const emailLike = nameDraft.includes('@');
  const canConnect =
    Boolean(picked) && Boolean(info) && !checking && !busy && !refused && !notGranted && toConnect.length > 0 && !emailLike && Boolean(nameDraft.trim());

  const addProduct = async () => {
    setError(null);
    try {
      const started = await Meteor.callPromise<{ authUrl?: string | null }>(
        'opsCommand/startGoogleOAuth',
        picked || restaurantId,
        'google',
        [request.property.kind],
        true,
      );
      if (started.authUrl) window.open(started.authUrl, 'aleno-google-oauth', 'width=520,height=740');
    } catch (e) {
      setError(errorText(e));
    }
  };

  const connect = async () => {
    if (!canConnect) return;
    setBusy(true);
    setError(null);
    try {
      const result = await connectProperty(restaurantId, request, toConnect, nameDraft.trim());
      onConnected(
        {
          restaurant,
          property: request.property.displayName,
          undo: result.undo ? [result.undo] : [],
        },
        result,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const alreadyNotes = chosenIds
    .filter(id => already.has(id))
    .map(id => {
      const name = byId.get(id)?.name || id;
      const via = info?.alreadyVia?.connectionName;
      return via
        ? t('accountConnect.already', { restaurant: name, connection: via })
        : t('accountConnect.alreadyUnnamed', { restaurant: name });
    });

  return (
    <>
      <div className="scrim acx-scrim" onClick={onClose} role="presentation" />
      <div
        ref={ref}
        className="acx-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-hj-suppress
      >
        <div className="acx-grip" aria-hidden="true" />
        <div className="acx-head">
          <h2 id={titleId} className="acx-title">
            {t('accountConnect.sheetTitle', { property: request.property.displayName, product })}
          </h2>
          <button type="button" className="ghost-btn" onClick={onClose} aria-label={t('accountConnect.close')}>
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="acx-body">
          <div className="acx-pick">
            <label htmlFor={pickId} className="acx-pick-label">
              {t('accountConnect.pickRestaurant')} <span>({t('accountConnect.onlyOpen')})</span>
            </label>
            <RestaurantCombobox
              id={pickId}
              restaurants={pickable}
              suggestions={request.suggestions || []}
              value={picked}
              onChange={setPicked}
              describedBy={sameCustomerOnly ? reasonId : undefined}
            />
            {sameCustomerOnly ? (
              <p className="acx-reason" id={reasonId} data-acx="pick-one-customer">
                {customerLabel(info?.customerName)
                  ? t('accountConnect.oneCustomer', { customer: customerLabel(info?.customerName) })
                  : t('accountConnect.oneCustomerUnnamed')}{' '}
                {t('accountConnect.comboUntick')}
              </p>
            ) : null}
          </div>

          {checking && picked ? (
            <p className="acx-muted" role="status">
              {t('accountConnect.checking')}
            </p>
          ) : null}

          {info && siblings.length ? (
            <fieldset className="acx-also">
              <legend>{t('accountConnect.alsoConnect')}</legend>
              {siblings.map(venue => {
                const on = extra.has(venue.id);
                const skip = already.has(venue.id);
                return (
                  <label key={venue.id} className={`acx-also-row${on ? ' on' : ''}${skip ? ' is-skip' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={skip}
                      onChange={() =>
                        setExtra(current => {
                          const next = new Set(current);
                          if (next.has(venue.id)) next.delete(venue.id);
                          else next.add(venue.id);
                          return next;
                        })
                      }
                    />
                    <span>{restaurantLabel(venue)}</span>
                  </label>
                );
              })}
              <p className="acx-reason" data-acx="one-customer">
                {customerLabel(info.customerName)
                  ? t('accountConnect.oneCustomer', { customer: customerLabel(info.customerName) })
                  : t('accountConnect.oneCustomerUnnamed')}
              </p>
            </fieldset>
          ) : info ? (
            <p className="acx-reason" data-acx="one-customer">
              {customerLabel(info.customerName)
                ? t('accountConnect.oneCustomer', { customer: customerLabel(info.customerName) })
                : t('accountConnect.oneCustomerUnnamed')}
            </p>
          ) : null}

          {refused ? (
            <p className="acx-refused" role="alert" data-acx="refused">
              {t('accountConnect.feedsOther', {
                property: request.property.displayName,
                restaurant: pickedOption?.name || '',
              })}
            </p>
          ) : null}
          {alreadyNotes.map(note => (
            <p key={note} className="acx-note" data-acx="already">
              {note}
            </p>
          ))}
          {info && !refused && chosenIds.length && !toConnect.length ? (
            <p className="acx-note">{t('accountConnect.alreadyAll')}</p>
          ) : null}
          {notGranted ? (
            <div className="acx-refused" role="alert" data-acx="not-granted">
              <p>{t('accountConnect.notGranted', { product })}</p>
              <button type="button" className="acx-quiet" onClick={() => void addProduct()}>
                {t('accountConnect.addProduct', { product })}
              </button>
            </div>
          ) : null}

          {info && !refused && toConnect.length ? (
            <>
              <label className="acx-name" htmlFor={nameId}>
                <span>{t('accountConnect.nameLabel')}</span>
                <input
                  id={nameId}
                  className="acx-input"
                  type="text"
                  maxLength={60}
                  value={nameDraft}
                  disabled={info.grantExists}
                  onChange={e => setNameDraft(e.target.value)}
                />
                <small className={emailLike ? 'is-error' : undefined}>
                  {emailLike ? t('accountConnect.nameNoEmail') : t('accountConnect.nameHint', { restaurant })}
                </small>
              </label>
              <section className="acx-what" aria-label={t('accountConnect.whatHappens')}>
                <h3>{t('accountConnect.whatHappens')}</h3>
                <ul>
                  <li>{t('accountConnect.bulletSee', { restaurant, product })}</li>
                  <li>
                    {info.grantExists
                      ? t('accountConnect.bulletConnectionExisting', { restaurant, name: nameDraft.trim() })
                      : t('accountConnect.bulletConnection', {
                          restaurant,
                          name: nameDraft.trim(),
                          by: target?.connectedByName || t('accountConnect.byYou'),
                        })}
                  </li>
                  <li>{t('accountConnect.bulletAccount', { restaurant })}</li>
                </ul>
              </section>
            </>
          ) : null}

          {error ? (
            <p className="acx-refused" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className="acx-actions">
          <button type="button" className="acx-quiet" onClick={onClose}>
            {t('accountConnect.cancel')}
          </button>
          <button type="button" className="acx-primary" disabled={!canConnect} onClick={() => void connect()}>
            {busy
              ? t('accountConnect.connecting')
              : restaurant
                ? t('accountConnect.connectTo', { restaurant })
                : t('accountConnect.connect')}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * "Connected to Kronenhalle · Undo". Undo unmaps what the connect added and
 * removes the customer's grant if that left it empty; both are logged.
 */
export function ConnectToast({
  restaurantId,
  toast,
  onDismiss,
  onUndone,
}: {
  restaurantId: string;
  toast: ConnectedToast;
  onDismiss: () => void;
  onUndone?: () => void;
}) {
  const { t } = useTranslation('growth');
  const [state, setState] = useState<'shown' | 'undoing' | 'undone' | 'failed'>('shown');
  useEffect(() => {
    if (state === 'undoing') return;
    const timer = window.setTimeout(onDismiss, state === 'shown' ? 12_000 : 6_000);
    return () => window.clearTimeout(timer);
  }, [state, onDismiss]);
  const undo = async () => {
    setState('undoing');
    try {
      for (const step of toast.undo) await undoConnect(restaurantId, step);
      setState('undone');
      onUndone?.();
    } catch {
      setState('failed');
    }
  };
  const text =
    state === 'undone'
      ? t('accountConnect.undone', { restaurant: toast.restaurant, property: toast.property })
      : state === 'failed'
        ? t('accountConnect.undoFailed', { restaurant: toast.restaurant })
        : toast.count && toast.count > 1
          ? t('accountConnect.connectedMany', { count: toast.count })
          : t('accountConnect.toastConnected', { restaurant: toast.restaurant });
  return (
    <div className="acx-toast" role="status" aria-live="polite" data-hj-suppress>
      <Icon name={state === 'undone' ? 'refresh' : 'check'} size={15} />
      <span>{text}</span>
      {state === 'shown' && toast.undo.length ? (
        <>
          <span aria-hidden="true">·</span>
          <button type="button" className="acx-toast-undo" onClick={() => void undo()}>
            {t('accountConnect.undo')}
          </button>
        </>
      ) : null}
      <button type="button" className="acx-toast-x" onClick={onDismiss} aria-label={t('accountConnect.close')}>
        <Icon name="close" size={13} />
      </button>
    </div>
  );
}
